import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';
import { formatINR, formatIndianDate, formatIndianNumber } from '../../../utils/currency';

const WarrantyDataGrid = ({ 
  warrantyData = [], 
  loading = false, 
  onAssetSelect, 
  selectedAssets = [], 
  onBulkAction,
  onRefresh 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'daysRemaining', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [updatingAsset, setUpdatingAsset] = useState(null);

  // Dynamic vendor options from real data
  const vendorOptions = useMemo(() => {
    const vendors = new Set(warrantyData.map(item => item.vendor).filter(Boolean));
    return [
      { value: 'all', label: 'All Vendors' },
      ...Array.from(vendors).sort().map(vendor => ({ value: vendor, label: vendor }))
    ];
  }, [warrantyData]);

  const statusOptions = [
    { value: 'all', label: 'All Renewal Statuses' },
    { value: 'Urgent', label: 'Urgent (≤30 Days)' },
    { value: 'Pending', label: 'Pending (31-60 Days)' },
    { value: 'In Progress', label: 'In Progress (61-90 Days)' },
    { value: 'Active', label: 'Active (>90 Days)' },
    { value: 'Expired', label: 'Expired' },
    { value: 'No Warranty', label: 'No Warranty' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'critical', label: 'Critical' },
    { value: 'warning', label: 'Warning' },
    { value: 'normal', label: 'Normal' }
  ];

  const filteredAndSortedData = useMemo(() => {
    let filtered = warrantyData.filter(item => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || 
        (item.id || '').toLowerCase().includes(q) ||
        (item.name || '').toLowerCase().includes(q) ||
        (item.assetType || '').toLowerCase().includes(q) ||
        (item.owner || '').toLowerCase().includes(q) ||
        (item.vendor || '').toLowerCase().includes(q) ||
        (item.model || '').toLowerCase().includes(q);

      const matchesStatus = statusFilter === 'all' || item.renewalStatus === statusFilter;
      const matchesVendor = vendorFilter === 'all' || item.vendor === vendorFilter;
      const matchesPriority = priorityFilter === 'all' || item.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesVendor && matchesPriority;
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'daysRemaining') {
          // Put nulls at the end
          if (aValue === null) return 1;
          if (bValue === null) return -1;
          aValue = Number(aValue);
          bValue = Number(bValue);
        } else if (sortConfig.key === 'cost') {
          aValue = a.costRaw || 0;
          bValue = b.costRaw || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [warrantyData, searchQuery, statusFilter, vendorFilter, priorityFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'normal':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Urgent':
        return 'text-error bg-error/10 border-error/20 font-bold';
      case 'Expired':
        return 'text-destructive bg-destructive/10 border-destructive/20';
      case 'Pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'In Progress':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'Active':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  // Real API single renewal
  const handleQuickRenew = async (assetId, assetCode) => {
    try {
      setUpdatingAsset(assetId);
      const res = await axiosInstance.post('/warranty/renew', {
        asset_id: assetId,
        period: '12months'
      });

      if (res.data?.success) {
        toast.success(res.data.message || `Warranty renewed for ${assetCode}!`);
        onRefresh?.();
      } else {
        toast.error('Failed to renew warranty');
      }
    } catch (error) {
      console.error('Renewal failed:', error);
      toast.error(error.response?.data?.message || 'Failed to renew warranty');
    } finally {
      setUpdatingAsset(null);
    }
  };

  const isAllSelected = selectedAssets.length > 0 && selectedAssets.length === filteredAndSortedData.length;
  const isIndeterminate = selectedAssets.length > 0 && selectedAssets.length < filteredAndSortedData.length;

  const handleSelectAll = (checked) => {
    if (checked) {
      onAssetSelect(filteredAndSortedData.map(item => item.id));
    } else {
      onAssetSelect([]);
    }
  };

  const handleSelectAsset = (assetId, checked) => {
    if (checked) {
      onAssetSelect([...selectedAssets, assetId]);
    } else {
      onAssetSelect(selectedAssets.filter(id => id !== assetId));
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mx-auto mb-3"></div>
        <p className="text-muted-foreground text-sm font-medium">Loading live warranty records...</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle overflow-hidden">
      {/* Search & Filter Controls */}
      <div className="p-5 border-b border-border bg-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Warranty Inventory & Action Grid</h3>
            <p className="text-xs text-muted-foreground">Track expiration dates, vendors, and trigger 1-click renewals</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <Input
              type="search"
              placeholder="Search asset, owner, vendor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-60"
            />
            
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Status"
              className="w-full sm:w-44"
            />
            
            <Select
              options={vendorOptions}
              value={vendorFilter}
              onChange={setVendorFilter}
              placeholder="Vendor"
              className="w-full sm:w-44"
            />
            
            <Select
              options={priorityOptions}
              value={priorityFilter}
              onChange={setPriorityFilter}
              placeholder="Priority"
              className="w-full sm:w-36"
            />
          </div>
        </div>

        {/* Bulk Action Strip */}
        {selectedAssets.length > 0 && (
          <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-semibold text-primary">
                {selectedAssets.length} asset{selectedAssets.length > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => onBulkAction('renew')}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Bulk Renew (+1 Year)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('quote')}
                iconName="FileText"
                iconPosition="left"
              >
                Request Quotes
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAssetSelect([])}
              >
                Deselect All
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-muted/40 border-b border-border text-muted-foreground uppercase tracking-wider font-semibold">
            <tr>
              <th className="w-10 p-3.5">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onCheckedChange={handleSelectAll}
                />
              </th>
              <th className="p-3.5">
                <button onClick={() => handleSort('id')} className="flex items-center space-x-1 hover:text-foreground">
                  <span>Asset ID</span>
                  <Icon name="ArrowUpDown" size={13} />
                </button>
              </th>
              <th className="p-3.5">
                <button onClick={() => handleSort('name')} className="flex items-center space-x-1 hover:text-foreground">
                  <span>Asset / Category</span>
                  <Icon name="ArrowUpDown" size={13} />
                </button>
              </th>
              <th className="p-3.5">
                <button onClick={() => handleSort('purchaseDate')} className="flex items-center space-x-1 hover:text-foreground">
                  <span>Purchase Date</span>
                  <Icon name="ArrowUpDown" size={13} />
                </button>
              </th>
              <th className="p-3.5">
                <button onClick={() => handleSort('warrantyEndDate')} className="flex items-center space-x-1 hover:text-foreground">
                  <span>Warranty Expiry</span>
                  <Icon name="ArrowUpDown" size={13} />
                </button>
              </th>
              <th className="p-3.5">
                <button onClick={() => handleSort('daysRemaining')} className="flex items-center space-x-1 hover:text-foreground">
                  <span>Timeline</span>
                  <Icon name="ArrowUpDown" size={13} />
                </button>
              </th>
              <th className="p-3.5">Status</th>
              <th className="p-3.5">Vendor</th>
              <th className="p-3.5 text-right">
                <button onClick={() => handleSort('cost')} className="flex items-center space-x-1 hover:text-foreground ml-auto">
                  <span>Price (₹)</span>
                  <Icon name="ArrowUpDown" size={13} />
                </button>
              </th>
              <th className="p-3.5 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredAndSortedData.length === 0 ? (
              <tr>
                <td colSpan="10" className="p-12 text-center">
                  <Icon name="Inbox" size={40} className="text-muted-foreground mx-auto mb-2 opacity-60" />
                  <p className="text-sm font-medium text-foreground">No warranty assets match your filters</p>
                  <p className="text-xs text-muted-foreground mt-1">Try clearing your search query or status filter.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('all');
                      setVendorFilter('all');
                      setPriorityFilter('all');
                    }}
                    className="mt-3"
                  >
                    Reset Filters
                  </Button>
                </td>
              </tr>
            ) : (
              filteredAndSortedData.map((asset) => (
                <tr key={asset.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="p-3.5">
                    <Checkbox
                      checked={selectedAssets.includes(asset.id)}
                      onCheckedChange={(checked) => handleSelectAsset(asset.id, checked)}
                    />
                  </td>
                  <td className="p-3.5">
                    <div className="font-bold text-card-foreground">{asset.id}</div>
                    <div className="text-[11px] text-muted-foreground truncate max-w-[120px]">{asset.owner}</div>
                  </td>
                  <td className="p-3.5">
                    <div className="font-semibold text-card-foreground truncate max-w-[170px]" title={asset.name}>
                      {asset.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {asset.assetType} • {asset.model}
                    </div>
                  </td>
                  <td className="p-3.5 text-card-foreground">
                    {formatIndianDate(asset.purchaseDate)}
                  </td>
                  <td className="p-3.5 font-medium text-card-foreground">
                    {asset.warrantyEndDate ? formatIndianDate(asset.warrantyEndDate) : (
                      <span className="text-muted-foreground italic">Not Set</span>
                    )}
                  </td>
                  <td className="p-3.5">
                    {asset.daysRemaining !== null ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${getPriorityBadge(asset.priority)}`}>
                        {asset.daysRemaining < 0
                          ? `Expired (${Math.abs(asset.daysRemaining)}d ago)`
                          : `${asset.daysRemaining} days left`}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-[11px]">N/A</span>
                    )}
                  </td>
                  <td className="p-3.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] border ${getStatusBadge(asset.renewalStatus)}`}>
                      {asset.renewalStatus}
                    </span>
                  </td>
                  <td className="p-3.5">
                    <div className="font-medium text-card-foreground truncate max-w-[120px]" title={asset.vendor}>
                      {asset.vendor}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{asset.department}</div>
                  </td>
                  <td className="p-3.5 text-right font-semibold text-card-foreground">
                    {formatINR(asset.costRaw, false)}
                  </td>
                  <td className="p-3.5 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2.5 text-xs border-primary/30 hover:bg-primary/10 hover:text-primary"
                      onClick={() => handleQuickRenew(asset.rawId || asset.id, asset.id)}
                      loading={updatingAsset === (asset.rawId || asset.id)}
                      iconName="RefreshCw"
                      iconPosition="left"
                    >
                      Renew
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Footer Summary */}
      <div className="p-4 border-t border-border bg-muted/20 flex flex-col sm:flex-row items-center justify-between text-xs text-muted-foreground gap-3">
        <span>
          Showing <b>{filteredAndSortedData.length}</b> of <b>{warrantyData.length}</b> assets
        </span>
        <div className="flex items-center space-x-4">
          <span className="flex items-center space-x-1.5">
            <span className="w-2 h-2 rounded-full bg-error" />
            <span>Urgent: <b>{filteredAndSortedData.filter(a => a.renewalStatus === 'Urgent').length}</b></span>
          </span>
          <span className="flex items-center space-x-1.5">
            <span className="w-2 h-2 rounded-full bg-warning" />
            <span>Pending: <b>{filteredAndSortedData.filter(a => a.renewalStatus === 'Pending').length}</b></span>
          </span>
          <span className="flex items-center space-x-1.5">
            <span className="w-2 h-2 rounded-full bg-success" />
            <span>Active: <b>{filteredAndSortedData.filter(a => a.renewalStatus === 'Active').length}</b></span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default WarrantyDataGrid;
